import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Infinity } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Relationship = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({
    person1: { name: "", day: "", month: "", year: "" },
    person2: { name: "", day: "", month: "", year: "" }
  });
  const [loading, setLoading] = useState(false);
  const [stars, setStars] = useState([]);

  useEffect(() => {
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 100; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();
  }, []);

  const updatePerson = (person, field, value) => {
    setFormData(prev => ({
      ...prev,
      [person]: { ...prev[person], [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.person1.name || !formData.person2.name) {
      toast.error("Veuillez remplir les deux prénoms");
      return;
    }
    if (!formData.person1.day || !formData.person1.month || !formData.person1.year ||
        !formData.person2.day || !formData.person2.month || !formData.person2.year) {
      toast.error("Veuillez remplir toutes les dates de naissance");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-relationship`, {
        person1: {
          name: formData.person1.name,
          day: parseInt(formData.person1.day),
          month: parseInt(formData.person1.month),
          year: parseInt(formData.person1.year)
        },
        person2: {
          name: formData.person2.name,
          day: parseInt(formData.person2.day),
          month: parseInt(formData.person2.month),
          year: parseInt(formData.person2.year)
        },
        language: language
      });

      navigate("/relationship-results", { state: { result: response.data } });
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            data-testid="back-home-button"
            onClick={() => navigate("/")}
            variant="outline"
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('btn_home')}
          </Button>
          <img
            src="/elua-logo.png"
            alt="ELUA"
            className="h-16 w-16 object-contain logo-pulse"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-4">
            <Infinity className="w-10 h-10 golden-accent" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 glow-text" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('title_square')}
          </h1>
          <p className="text-xl text-white/80">{t('subtitle_square')}</p>
        </div>

        {/* Form */}
        <Card className="glass-card max-w-4xl mx-auto border-2 golden-border" data-testid="relationship-form">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Person 1 */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-semibold text-[#F4C430] mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
                    {t('label_person1')}
                  </h3>
                  <div>
                    <Label htmlFor="person1-name" className="text-white/90 text-base mb-2 block">
                      {t('label_name').replace(' (optionnel)', '').replace(' (optional)', '').replace(' (opcional)', '')}
                    </Label>
                    <Input
                      id="person1-name"
                      data-testid="person1-name-input"
                      type="text"
                      placeholder="Prénom"
                      value={formData.person1.name}
                      onChange={(e) => updatePerson('person1', 'name', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 text-base mb-2 block">
                      {t('label_birthdate')}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        data-testid="person1-day-input"
                        type="number"
                        placeholder={t('placeholder_day')}
                        min="1"
                        max="31"
                        value={formData.person1.day}
                        onChange={(e) => updatePerson('person1', 'day', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                      <Input
                        data-testid="person1-month-input"
                        type="number"
                        placeholder={t('placeholder_month')}
                        min="1"
                        max="12"
                        value={formData.person1.month}
                        onChange={(e) => updatePerson('person1', 'month', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                      <Input
                        data-testid="person1-year-input"
                        type="number"
                        placeholder={t('placeholder_year')}
                        min="1900"
                        max="2100"
                        value={formData.person1.year}
                        onChange={(e) => updatePerson('person1', 'year', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Person 2 */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-semibold text-[#F4C430] mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
                    {t('label_person2')}
                  </h3>
                  <div>
                    <Label htmlFor="person2-name" className="text-white/90 text-base mb-2 block">
                      {t('label_name').replace(' (optionnel)', '').replace(' (optional)', '').replace(' (opcional)', '')}
                    </Label>
                    <Input
                      id="person2-name"
                      data-testid="person2-name-input"
                      type="text"
                      placeholder={t('label_name').replace(' (optionnel)', '').replace(' (optional)', '').replace(' (opcional)', '')}
                      value={formData.person2.name}
                      onChange={(e) => updatePerson('person2', 'name', e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 text-base mb-2 block">
                      {t('label_birthdate')}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        data-testid="person2-day-input"
                        type="number"
                        placeholder={t('placeholder_day')}
                        min="1"
                        max="31"
                        value={formData.person2.day}
                        onChange={(e) => updatePerson('person2', 'day', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                      <Input
                        data-testid="person2-month-input"
                        type="number"
                        placeholder={t('placeholder_month')}
                        min="1"
                        max="12"
                        value={formData.person2.month}
                        onChange={(e) => updatePerson('person2', 'month', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                      <Input
                        data-testid="person2-year-input"
                        type="number"
                        placeholder={t('placeholder_year')}
                        min="1900"
                        max="2100"
                        value={formData.person2.year}
                        onChange={(e) => updatePerson('person2', 'year', e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                data-testid="calculate-relationship-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold py-6 text-lg"
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                {loading ? t('btn_calculate_loading') : t('btn_discover_link')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Relationship;
